import { HTTPMethod } from '@perpay-web/constants/httpMethods';
import { GOOGLE_PLACES_KEY } from '@perpay-web/constants/serviceCredentials';
import { GOOGLE_PLACE_DETAILS_URL } from '@perpay-web/constants/urls';
import { getData, useDataModule } from '@perpay-web/data-module/useDataModule';
import { useReduxDataModuleHookConnection } from '@perpay-web/data-module/useReduxDataModuleHookConnection';
import {
    fetchUserAddressDetails,
    fetchUserAddressDetailsSuccess,
    fetchUserAddressDetailsError,
    fetchUserAddressDetailsReset,
} from '@perpay-web/hooks/actions/addressDetails';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { jsonFetch } from '@perpay-web/utils/requestUtils';

export const getAddressDetails = (state) => getData(state);

const useAddressDetailsDataModule = () => {
    const dataModule = useDataModule(async ({ placeId }) => {
        if (!placeId) {
            return {
                street1: '',
                street2: '',
                city: '',
                state: '',
                zipcode: '',
            };
        }
        const url = new URL(GOOGLE_PLACE_DETAILS_URL + placeId);
        const params = url.searchParams;
        params.append('fields', 'address_components');

        const addressData = await jsonFetch(url, {}, HTTPMethod.get, {
            'X-Goog-Api-Key': GOOGLE_PLACES_KEY,
        });

        const simplifiedObject = addressData.addressComponents.reduce(
            (acc, component) => {
                component.types.forEach((type) => {
                    acc[type] = {
                        longText: component.longText,
                        shortText: component.shortText,
                    };
                });
                return acc;
            },
            {},
        );

        const streetNumberLong =
            (simplifiedObject.street_number &&
                simplifiedObject.street_number.longText) ||
            '';
        const routeLong =
            (simplifiedObject.route && simplifiedObject.route.longText) || '';
        const premiseLong =
            (simplifiedObject.premise && simplifiedObject.premise.longText) ||
            '';
        const street1 =
            `${streetNumberLong} ${routeLong}`.trim() || premiseLong;

        const street2 =
            (simplifiedObject.subpremise &&
                simplifiedObject.subpremise.longText) ||
            '';
        const city =
            (simplifiedObject.locality && simplifiedObject.locality.longText) ||
            (simplifiedObject.neighborhood &&
                simplifiedObject.neighborhood.longText) ||
            '';
        const state =
            (simplifiedObject.administrative_area_level_1 &&
                simplifiedObject.administrative_area_level_1.shortText) ||
            '';
        const zipcode =
            (simplifiedObject.postal_code &&
                simplifiedObject.postal_code.longText) ||
            '';

        return {
            street1,
            street2,
            city,
            state,
            zipcode,
        };
    }, {});

    useReduxDataModuleHookConnection({
        dataModule,
        hookAction$,
        request: fetchUserAddressDetails,
        success: fetchUserAddressDetailsSuccess,
        error: fetchUserAddressDetailsError,
        reset: fetchUserAddressDetailsReset,
    });

    return dataModule;
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const dataModule = useAddressDetailsDataModule();

        return (
            <Context.Provider value={dataModule}>{children}</Context.Provider>
        );
    };

    return Provider;
};

export const [UseAddressDetailsDataModuleProvider, useAddressDetailsContext] =
    createContextProvider(providerFactory);
